import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useAdminAIUsage } from '@/hooks/useAdmin';
import { Card } from '@/components/ui/Card';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatCurrency } from '@/utils/format';

export function AdminAIPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useAdminAIUsage();

  if (isError) return <ErrorState onRetry={() => refetch()} />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('ai.title')}</h1>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title={t('ai.totalRequests')} value={data ? String(data.totalRequests) : '0'} icon={SparklesIcon} isLoading={isLoading} />
        <StatCard title={t('ai.totalCost')} value={data ? formatCurrency(data.totalCost) : '-'} isLoading={isLoading} />
        <StatCard title={t('ai.requests')} value={data ? String(data.requestsByFeature?.length ?? 0) + ' ' + t('ai.features') : '-'} isLoading={isLoading} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title={t('ai.requestsByFeature')}>
          {isLoading ? (
            <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full rounded" />)}</div>
          ) : data?.requestsByFeature?.length > 0 ? (
            <div className="space-y-3">
              {(() => {
                const max = Math.max(...data.requestsByFeature.map((r: { count: number }) => r.count), 1);
                return data.requestsByFeature.map((item: { feature: string; count: number }) => (
                  <div key={item.feature}>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">{item.feature}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{item.count}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800">
                      <div className="h-2 rounded-full bg-brand-500" style={{ width: `${(item.count / max) * 100}%` }} />
                    </div>
                  </div>
                ));
              })()}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">-</p>
          )}
        </Card>

        <Card title={t('ai.costByUser')}>
          {isLoading ? (
            <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full rounded" />)}</div>
          ) : data?.costByUser?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-3 py-2">{t('admin.user')}</th>
                    <th className="px-3 py-2 text-right">{t('ai.cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.costByUser.map((item: { user: string; cost: number }) => (
                    <tr key={item.user} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{item.user}</td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">-</p>
          )}
        </Card>
      </div>

      {/* Settings link */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cog6ToothIcon className="h-5 w-5 text-gray-400" />
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">{t('ai.aiEnabled')}, {t('ai.defaultModel')}, {t('ai.maxTokens')}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.appSettings')}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/settings')}>{t('admin.settings')}</Button>
        </div>
      </Card>
    </div>
  );
}
